import { Loader as MapsLoader } from '@googlemaps/js-api-loader';
import FormField from './field.js';
import DealerMapper from '../../utils/dealer-mapper.js';
import AnalyticsHelper from '../../utils/analytics-helper';
import OpeningHours from '../../utils/opening-hours';
import LinkHelper from '../../utils/link-helper';
import { scrollParentToChild } from '../../utils/scroll-helper.js';

export default class DealerSearch extends FormField {

    constructor(parent, el) {
        super(parent, el);
        this.data = {};
        this.searchUrl = el.dataset.searchUrl;
        this.getUrl = el.dataset.getUrl;
        this.closedLabel = el.dataset.closedLabel;
        this.contactLabel = el.dataset.contactLabel;
        this.input = el.querySelector('.dealer_search_input');
        this.inputId = this.input.id;
        this.submit = el.querySelector('.dealer_search_submit');
        this.location = el.querySelector('.dealer_search_location');
        this.result = el.querySelector('.dealer_search_result');
        this.filters = el.querySelector('.dealer_search_filters');
        this.dropdown = el.querySelector('.dealer_search_dropdown');
        this.loader = el.querySelector('.dealer_search_loader');
        this.selected = el.querySelector('.dealer_search_selected');
        this.selectedDealerDetails = el.querySelector('.selected_dealer_details');
        this.alternateDealerLabel = el.querySelector('.alternate_dealer_label');
        this.dropdownTemplate = _.template(el.querySelector('#dealer_search_dropdown_template').innerHTML.replaceAll('\\', ''));
        this.selectedTemplate = _.template(el.querySelector('#dealer_search_selected_template').innerHTML.replaceAll('\\', ''));
        this.selectedDealerDetailsTemplateEl = el.querySelector('#selected_dealer_details_template');
        this.selectedDealerDetailsTemplate = this.selectedDealerDetailsTemplateEl ? _.template(this.selectedDealerDetailsTemplateEl?.innerHTML.replaceAll('\\', '')) : null ;
        this.error = el.querySelector('.form_field_error_message');
        this.regexIgnoreCase = this.input.dataset.regexIgnoreCase !== undefined ? 'i' : '';
        this.regex = this.input.dataset.regex ? new Regex(this.input.dataset.regex, this.regexIgnoreCase) : null;
        this.defaultError = this.error.textContent;
        this.regexError = this.input.dataset.regexError;
        this.autocomplete();
        this.currentLocation();
        this.bind();
        this.populate();
    }

    autocomplete() {
        if (this.input.dataset.autocomplete) {
            const mapsLoader = new MapsLoader({ apiKey: this.input.dataset.mapapikey, version: 'weekly', libraries: ['places'] });
            mapsLoader.load().then(() => {
                new google.maps.places.Autocomplete(this.input, { types: ['geocode', 'establishment'], componentRestrictions: { country: this.input.dataset.mapregion } });
            });
        }
    }

    currentLocation() {
        if (navigator.geolocation && window.location.protocol === 'https:') {
            this.location.addEventListener('click', (e) => {
                e.preventDefault();
                navigator.geolocation.getCurrentPosition((pos) => {
                    const lat = pos.coords.latitude;
                    const lng = pos.coords.longitude;
                    const value = `${lat} ${lng}`;
                    this.input.value = value;
                    this.search(e);
                });
            });
        } else {
            this.location.classList.add('hide');
        }
    }

    bind() {
        this.submit.addEventListener('click', (e) => this.search(e));
        this.result.addEventListener('change', (e) => this.populate());
        this.input.addEventListener('keydown', (e) => e.key === 'Enter' ? this.search(e) : null);
        this.dropdown.addEventListener('keydown', (e) => this.handleDropdown(e));
    }

    search(e) {
        e.preventDefault();
        if(!this.input.value){
            this.error.textContent = this.defaultError;
            this.toggle(false);
        } else if(this.regex && !this.regex?.test(this.input.value)){
            this.error.textContent = this.regexError;
            this.toggle(false);
        } else {
            this.loader.classList.remove('hide');
            this.toggle(true);
            this.analyticsPush();
            const data = new FormData();
            data.append('q', this.input.value);
            data.append('filters', this.filters.value);
            return fetch(this.searchUrl, { method: 'POST', body: data })
                .then((response) => response.json())
                .then((results) => results ? results.map((result) => DealerMapper.map(result)) : [])
                .then((dealers) => this.list(dealers))
                .catch((err) => this.reset(err));
        }
    }

    save(dealers) {
        dealers.forEach((d) => this.data[d.uri] = d);
    }

    list(dealers) {
        this.save(dealers);
        this.loader.classList.add('hide');
        this.dropdown.classList.remove('hide');
        this.input.setAttribute('aria-expanded', true);
        this.selected.classList.add('hide');
        this.selectedDealerDetails?.classList?.add('hide');
        this.alternateDealerLabel?.classList?.add('hide');
        this.dropdown.innerHTML = this.dropdownTemplate({ dealers });
        this.setFirstAriaActiveDecendant();
        this.dropdown.querySelectorAll('li').forEach((el) => el.addEventListener('click', (e) => this.select(el)));
    }

    getAriaActiveDecendant() {
       return this.dropdown.querySelector('[aria-selected="true"]');
    }

    setFirstAriaActiveDecendant() {
        const first = this.dropdown.querySelector('li');
        this.dropdown.setAttribute('aria-activedescendant', first?.id);
        first?.setAttribute('aria-selected', 'true');
    }

    navigateAriaActiveDecendant(direction) {
        const selected = this.dropdown.querySelector('[aria-selected="true"]') ?? this.dropdown.querySelector('li');
        const sibling = direction === 'next' ? selected.nextElementSibling : selected.previousElementSibling;
        if(sibling) {
            this.dropdown.setAttribute('aria-activedescendant', sibling.id);
            selected.removeAttribute('aria-selected');
            sibling.setAttribute('aria-selected', 'true');
            scrollParentToChild(this.dropdown, sibling);
        }
    }

    handleDropdown(event) {
        if(event.key === 'ArrowUp') {
            event.preventDefault();
            this.navigateAriaActiveDecendant('prev');
        } else if(event.key === 'ArrowDown') {
            event.preventDefault();
            this.navigateAriaActiveDecendant('next');
        } else if(event.key === ' ' || event.key === 'Enter') {
            event.preventDefault();
            this.select(this.getAriaActiveDecendant());
            this.input.focus();
        }
    }

    select(el) {
        this.result.value = el.dataset.uri;
        this.result.dispatchEvent(new Event('change'));
        this.analyticsPush(el);
    }

    async populate() {
        const dealer = this.result.value;
        if (!dealer) { return; }
        this.data[dealer] ? this.set() : this.get(dealer);
    }

    get(dealer) {
        this.loader.classList.remove('hide');
        const data = new FormData();
        data.append('dealer', dealer);
        data.append('filters', this.filters.value);
        return fetch(this.getUrl, { method: 'POST', body: data })
            .then((response) => response.json())
            .then((result) => DealerMapper.map(result))
            .then((dealer) => this.save([dealer]))
            .then(() => this.set())
            .catch(() => this.reset());
    }

    set() {
        this.toggle(true);
        const dealer = this.result.value;
        const data = this.data[dealer];
        if(data) {
            this.selected.innerHTML = this.selectedTemplate({ dealer: data });
            this.selected.classList.remove('hide');
            if(this.selectedDealerDetails && this.selectedDealerDetailsTemplate){
                this.selectedDealerDetails.innerHTML = this.selectedDealerDetailsTemplate({
                    dealer: data,
                    openingHours : OpeningHours.html(data.openingHours, this.closedLabel),
                    contactData : this.getContactData(data),
                 });
                this.selectedDealerDetails?.classList?.remove('hide');
            }
            this.alternateDealerLabel?.classList?.remove('hide');
            this.dropdown.classList.add('hide');
            this.input.setAttribute('aria-expanded', false);
            this.loader.classList.add('hide');
            this.update();
        } else {
            this.reset();
        }
    }

    getContactData(data) {
        if (data.contactForm) {
            return {
                textContent : this.contactLabel,
                href : `${LinkHelper.dotHtml(data.contactForm)}?dealerId=${data.uri}`
            }

        } else if (this.contactForm) {
            return {
                textContent : this.contactLabel,
                href : `${LinkHelper.dotHtml(this.contactForm)}?dealerId=${data.uri}`,
            }
        } else {
            return {
                textContent : data.email,
                href : `mailto:${data.email}`,
            }
        }
    }

    reset(err, change){
        this.selected.classList.add('hide');
        this.selectedDealerDetails?.classList?.add('hide');
        this.alternateDealerLabel?.classList?.add('hide');
        this.dropdown.classList.add('hide');
        this.input.setAttribute('aria-expanded', false);
        this.loader.classList.add('hide');
        this.result.value = "";
        if(change != false){
            this.result.dispatchEvent(new Event('change'));
        }
    }

    analyticsPush(value) {
        if(value) {
            AnalyticsHelper.push({
                event:'find_a_dealer_dealer_selection_complete',
                eventCategory:'find a dealer',
                eventAction:'Dealer Selection',
                eventLabel:`${dataLayer[0].productCategory}|Dealer selection complete|${this.input.value}|${value.querySelector('.dealer_search_dropdown_title').innerText}|internal|#`,
                actionType:'Dealer Selection',
                businessUnit : dataLayer[0].productCategory,
                moduleName:'Dealer selection complete',
                FADSearchText:`${this.input.value}`,
                clickText:`${value.querySelector('.dealer_search_dropdown_title').innerText}`,
                clickType:'internal',
                destinationURL:'#',
            });
        } else {
            AnalyticsHelper.push({
                event:'find_a_dealer',
                eventCategory:'find a dealer',
                eventAction:'Search',
                eventLabel:`${dataLayer[0].productCategory}|Dealer search|${this.input.value}|search|internal|#`,
                actionType:'Search',
                businessUnit : dataLayer[0].productCategory,
                moduleName:'Dealer search',
                FADSearchText:`${this.input.value}`,
                clickText:'Search',
                clickType:'internal',
                destinationURL:'#',
            });
        }


    }

    static is(el) {
        return super.is(el) && el.classList.contains('form_field_dealer_search');
    }
}
